import { useState, useEffect } from 'react';
import { apiFetch } from '../api';
import WeatherIcon from './WeatherIcon';

const WEATHER_CODES = {
  0: { label: 'Clear', icon: 'sunny' },
  1: { label: 'Mostly Clear', icon: 'sunny' },
  2: { label: 'Partly Cloudy', icon: 'partly-cloudy' },
  3: { label: 'Overcast', icon: 'cloudy' },
  45: { label: 'Foggy', icon: 'foggy' },
  48: { label: 'Icy Fog', icon: 'foggy' },
  51: { label: 'Light Drizzle', icon: 'rainy' },
  53: { label: 'Drizzle', icon: 'rainy' },
  55: { label: 'Heavy Drizzle', icon: 'rainy' },
  61: { label: 'Light Rain', icon: 'rainy' },
  63: { label: 'Rain', icon: 'rainy' },
  65: { label: 'Heavy Rain', icon: 'rainy' },
  71: { label: 'Light Snow', icon: 'snowy' },
  73: { label: 'Snow', icon: 'snowy' },
  75: { label: 'Heavy Snow', icon: 'snowy' },
  80: { label: 'Showers', icon: 'rainy' },
  81: { label: 'Moderate Showers', icon: 'rainy' },
  82: { label: 'Heavy Showers', icon: 'rainy' },
  85: { label: 'Snow Showers', icon: 'snowy' },
  86: { label: 'Heavy Snow Showers', icon: 'snowy' },
  95: { label: 'Thunderstorm', icon: 'stormy' },
  96: { label: 'Hail Thunderstorm', icon: 'stormy' },
  99: { label: 'Heavy Hail Thunderstorm', icon: 'stormy' },
};

function HourlyWeather({ refreshKey }) {
  const [hours, setHours] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function fetchWeather() {
      try {
        const res = await apiFetch('/api/weather');
        if (!res.ok) return;
        const data = await res.json();

        if (!data.hourly) return;

        // Open-Meteo returns times in the requested timezone (America/Chicago)
        // without a timezone suffix — compare using the raw strings to avoid
        // browser timezone mismatches, especially across midnight
        const nowChicago = new Date(new Date().toLocaleString('en-US', { timeZone: 'America/Chicago' }));
        const nowHour = `${nowChicago.getFullYear()}-${String(nowChicago.getMonth() + 1).padStart(2, '0')}-${String(nowChicago.getDate()).padStart(2, '0')}T${String(nowChicago.getHours()).padStart(2, '0')}:00`;

        // Find the index of the current or next hour by string comparison
        const startIdx = data.hourly.time.findIndex((t) => t >= nowHour);

        if (startIdx === -1) return;

        const upcoming = [];
        for (let i = startIdx; i < startIdx + 8 && i < data.hourly.time.length; i++) {
          const timeStr = data.hourly.time[i]; // e.g. "2026-04-08T01:00"
          // Parse hour directly from the string — already in Chicago time from Open-Meteo
          const hour = parseInt(timeStr.slice(11, 13), 10);
          const ampm = hour === 0 ? '12 AM' : hour < 12 ? `${hour} AM` : hour === 12 ? '12 PM' : `${hour - 12} PM`;
          const code = WEATHER_CODES[data.hourly.weather_code[i]] || { label: 'Unknown', icon: 'cloudy' };
          upcoming.push({
            timeStr,
            label: ampm,
            temp: Math.round(data.hourly.temperature_2m[i]),
            code,
            precip: data.hourly.precipitation_probability[i],
            wind: Math.round(data.hourly.wind_speed_10m[i]),
          });
        }

        setHours(upcoming);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    }
    fetchWeather();
  }, [refreshKey]);

  if (loading) return <section className="card"><h2>Hourly Forecast</h2><p className="loading">Loading...</p></section>;
  if (hours.length === 0) return <section className="card"><h2>Hourly Forecast</h2><p className="empty">No data</p></section>;

  return (
    <section className="card hourly-section">
      <h2>Hourly Forecast</h2>
      <div className="hourly-list">
        {hours.map((h, i) => (
          <div key={i} className="hourly-item">
            <span className="hourly-time">{i === 0 ? 'Now' : h.label}</span>
            <div className="hourly-icon">
              <WeatherIcon type={h.code.icon} size={32} />
            </div>
            <span className="hourly-temp">{h.temp}°</span>
            <span className="hourly-detail">{h.code.label}</span>
            <span className="hourly-precip">{h.precip}%</span>
            <span className="hourly-wind">{h.wind} mph</span>
          </div>
        ))}
      </div>
    </section>
  );
}

export default HourlyWeather;
